'use client';

export type AnalyticsConsentState = 'denied' | 'granted';

type StoredAnalyticsConsent = {
  analyticsStorage: AnalyticsConsentState;
  updatedAt: number;
};

const ANALYTICS_CONSENT_STORAGE_KEY = 'imgenhancer.analytics.consent.v2';

function isBrowser() {
  return typeof window !== 'undefined';
}

function normalizeConsentState(
  value: string | null | undefined
): AnalyticsConsentState | null {
  if (value === 'granted' || value === 'denied') {
    return value;
  }

  return null;
}

function readStoredAnalyticsConsent(): StoredAnalyticsConsent | null {
  if (!isBrowser()) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredAnalyticsConsent>;
    const analyticsStorage = normalizeConsentState(parsed.analyticsStorage);

    if (!analyticsStorage) {
      return null;
    }

    return {
      analyticsStorage,
      updatedAt:
        typeof parsed.updatedAt === 'number' &&
        Number.isFinite(parsed.updatedAt)
          ? parsed.updatedAt
          : Date.now(),
    };
  } catch {
    return null;
  }
}

function writeStoredAnalyticsConsent(state: StoredAnalyticsConsent) {
  if (!isBrowser()) {
    return;
  }

  try {
    window.localStorage.setItem(
      ANALYTICS_CONSENT_STORAGE_KEY,
      JSON.stringify(state)
    );
  } catch {
    // Ignore storage write failures.
  }
}

export function readAnalyticsConsentState() {
  return (
    readStoredAnalyticsConsent()?.analyticsStorage ??
    ('granted' satisfies AnalyticsConsentState)
  );
}

export function saveAnalyticsConsentState(
  analyticsStorage: AnalyticsConsentState
) {
  writeStoredAnalyticsConsent({
    analyticsStorage,
    updatedAt: Date.now(),
  });

  return analyticsStorage;
}
